/**
 * Launching a cloud production (PLAN §9.2, §10.2 screen 5, D11, D25's "launch
 * with a cost estimate"): a studio of the cloud site, what it runs (a rundown
 * or a playlist), when (now, or a scheduled start — the task launches
 * `PRELAUNCH_MINUTES` before it), redundant or not (off by default, D11), the
 * task size with its price, rehearsal, the simulated devices, and the safety
 * stop. The estimate follows every choice; the static egress IPs to allowlist
 * are shown beside it.
 *
 * The choices are local UI state, fresh on every opening; the request is the
 * container's (`onLaunch`).
 */
import { PRELAUNCH_MINUTES, type CloudTaskSizeWire, type CloudTaskSizeInfoWire } from '@norna/api-contract/cloudProductions.js';
import { CLOUD_SITE_ID } from '@norna/api-contract/sites.js';
import { type FormEvent, useState } from 'react';
import {
  type CloudLaunchRequest,
  describeEstimate,
  formatHourlyUsd,
  formatUsd,
  hourlyEstimate,
  launchRequestOf,
  launchTimeOf,
  localInputToIso,
  MAX_SAFETY_HOURS,
  MIN_SAFETY_HOURS,
  safetyStopHours,
  launchableSizes,
  type LaunchableSize,
} from '../../lib/cloudProductions.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { shownShowStyle } from '../../lib/showStyle/pick.js';
import { ShowStylePicker } from '../showStyles/ShowStylePicker.js';

export type Choice = { readonly id: string; readonly name: string };
export type LaunchStudio = Choice & { readonly siteId: string | null; readonly defaultShowStyleId: string | null };

export type LaunchCloudProductionDialogProps = {
  readonly open: boolean;
  /** Every studio; only the cloud site's are offered (§9.1). */
  readonly studios: readonly LaunchStudio[];
  /** The tenant's show styles (D32); the studio's default is pre-selected. */
  readonly showStyles: readonly Choice[];
  readonly rundowns: readonly Choice[];
  readonly playlists: readonly Choice[];
  /** The task sizes with their prices (`network.sizes`); empty while they load. */
  readonly sizes: readonly CloudTaskSizeInfoWire[];
  /** The NAT gateway's static addresses (D11); empty while they load. */
  readonly egressIps: readonly string[];
  readonly region: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onLaunch: (request: CloudLaunchRequest) => void;
  readonly onCancel: () => void;
};

/** The safety stop's default: the contract's 12 hours. */
const DEFAULT_HOURS = '12';

const SIZE_LABEL: Readonly<Record<CloudTaskSizeWire, string>> = { small: 'Small', medium: 'Medium', large: 'Large' };

function sizeLabel(size: LaunchableSize): string {
  return `${SIZE_LABEL[size.taskSize]} — ${String(size.vcpu)} vCPU, ${String(size.memoryGb)} GB — ${formatHourlyUsd(size.hourlyUsd)}/h per task`;
}

function SourcePicker(props: {
  readonly kind: 'rundown' | 'playlist';
  readonly sources: readonly Choice[];
  readonly sourceId: string;
  readonly disabled: boolean;
  readonly onKind: (kind: 'rundown' | 'playlist') => void;
  readonly onSource: (id: string) => void;
}) {
  const { kind, sources, sourceId, disabled } = props;
  const noun = kind === 'rundown' ? 'Rundown' : 'Playlist';
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">Runs</legend>
      <div className="flex gap-4 text-sm">
        {(['rundown', 'playlist'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="radio" name="cloud-source-kind" checked={kind === k} disabled={disabled} onChange={() => props.onKind(k)} />
            {k === 'rundown' ? 'A rundown' : 'A playlist'}
          </label>
        ))}
      </div>
      <NativeSelect aria-label={noun} value={sourceId} onChange={(e) => props.onSource(e.target.value)} disabled={disabled || sources.length === 0}>
        {sources.length === 0 ? <option value="">No {noun.toLowerCase()}s</option> : null}
        {sources.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </NativeSelect>
    </fieldset>
  );
}

function StartPicker(props: { readonly scheduled: boolean; readonly startLocal: string; readonly disabled: boolean; readonly onScheduled: (scheduled: boolean) => void; readonly onStart: (value: string) => void }) {
  const { scheduled, startLocal, disabled } = props;
  const startIso = localInputToIso(startLocal);
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">Start</legend>
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="radio" name="cloud-start" checked={!scheduled} disabled={disabled} onChange={() => props.onScheduled(false)} />
          Start now
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="cloud-start" checked={scheduled} disabled={disabled} onChange={() => props.onScheduled(true)} />
          Scheduled
        </label>
      </div>
      {scheduled ? (
        <Field>
          <Label htmlFor="cloud-start-at">Show starts at</Label>
          <Input id="cloud-start-at" type="datetime-local" value={startLocal} onChange={(e) => props.onStart(e.target.value)} disabled={disabled} />
          <FieldDescription data-testid="cloud-launch-time">
            {startIso === null
              ? `The task launches ${String(PRELAUNCH_MINUTES)} min before the start, so the node is ready on time.`
              : `The task launches at ${launchTimeOf(startIso).toLocaleString()} — ${String(PRELAUNCH_MINUTES)} min before the start.`}
          </FieldDescription>
        </Field>
      ) : null}
    </fieldset>
  );
}

function Estimate({ size, redundant, hours }: { readonly size: CloudTaskSizeInfoWire | undefined; readonly redundant: boolean; readonly hours: number | null }) {
  if (size === undefined) return <p className="text-sm text-muted-foreground">Prices are loading…</p>;
  const hourly = hourlyEstimate(size.hourlyUsd, redundant);
  return (
    <div className="grid gap-1 rounded-md border p-3 text-sm" data-testid="cloud-estimate-box">
      <span className="font-medium" data-testid="cloud-estimate">
        {describeEstimate(size.hourlyUsd, redundant)}
      </span>
      <span className="text-muted-foreground">
        {redundant ? 'Two tasks' : 'One task'}, on-demand Fargate prices; an estimate, not the invoice.
        {hours === null ? '' : ` At most ${formatUsd(hourly * hours)} before the automatic stop.`}
      </span>
    </div>
  );
}

function EgressIps({ egressIps, region }: { readonly egressIps: readonly string[]; readonly region: string | null }) {
  return (
    <div className="grid gap-1 text-sm" data-testid="cloud-egress">
      <span className="font-medium">Allowlist these addresses</span>
      <span className="text-muted-foreground">
        The production’s traffic to your devices and services leaves {region === null ? 'AWS' : `AWS ${region}`} from these static IPs — allow them through your firewalls.
      </span>
      {egressIps.length === 0 ? <span className="text-muted-foreground">(loading…)</span> : <span className="font-mono">{egressIps.join(', ')}</span>}
    </div>
  );
}

function StudioPicker(props: { readonly studios: readonly Choice[]; readonly studioId: string; readonly disabled: boolean; readonly onStudio: (id: string) => void }) {
  const { studios, studioId, disabled } = props;
  const none = studios.length === 0;
  return (
    <Field>
      <Label htmlFor="cloud-studio">Studio</Label>
      <NativeSelect id="cloud-studio" value={studioId} onChange={(e) => props.onStudio(e.target.value)} disabled={disabled || none}>
        {none ? <option value="">No cloud studios</option> : null}
        {studios.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </NativeSelect>
      {none ? (
        <FieldDescription data-testid="no-cloud-studios">A cloud production runs in a studio of the cloud site. Create a studio with the site “{CLOUD_SITE_ID}” first.</FieldDescription>
      ) : null}
    </Field>
  );
}

function SafetyStop(props: { readonly text: string; readonly valid: boolean; readonly disabled: boolean; readonly onText: (text: string) => void }) {
  const { text, valid, disabled } = props;
  return (
    <Field invalid={!valid}>
      <Label htmlFor="cloud-hours">Stop automatically after (hours)</Label>
      <Input id="cloud-hours" type="number" min={MIN_SAFETY_HOURS} max={MAX_SAFETY_HOURS} step="any" value={text} onChange={(e) => props.onText(e.target.value)} disabled={disabled} aria-invalid={!valid} />
      <FieldError>{valid ? null : `Between ${String(MIN_SAFETY_HOURS)} and ${String(MAX_SAFETY_HOURS)} hours.`}</FieldError>
    </Field>
  );
}

function LaunchForm({ studios, showStyles, rundowns, playlists, sizes: offered, egressIps, region, pending, error, onLaunch, onCancel }: Omit<LaunchCloudProductionDialogProps, 'open'>) {
  const sizes = launchableSizes(offered);
  const cloudStudios = studios.filter((s) => s.siteId === CLOUD_SITE_ID);
  const [pickedStudio, setStudioId] = useState('');
  const [pickedShowStyle, setShowStyleId] = useState('');
  const [kind, setKind] = useState<'rundown' | 'playlist'>(rundowns.length > 0 || playlists.length === 0 ? 'rundown' : 'playlist');
  const [pickedSource, setSourceId] = useState('');
  const [scheduled, setScheduled] = useState(false);
  const [startLocal, setStartLocal] = useState('');
  const [redundant, setRedundant] = useState(false);
  const [pickedSize, setSize] = useState<CloudTaskSizeWire>('small');
  const [rehearsal, setRehearsal] = useState(false);
  const [simulators, setSimulators] = useState(false);
  const [hoursText, setHours] = useState(DEFAULT_HOURS);
  const sources = kind === 'rundown' ? rundowns : playlists;
  // What is shown selected: the pick, else the first choice — the lists may arrive after the dialog opened.
  const studioId = cloudStudios.some((s) => s.id === pickedStudio) ? pickedStudio : (cloudStudios[0]?.id ?? '');
  const studioDefault = cloudStudios.find((s) => s.id === studioId)?.defaultShowStyleId ?? null;
  const showStyleId = shownShowStyle(pickedShowStyle, studioDefault, showStyles);
  const sourceId = sources.some((s) => s.id === pickedSource) ? pickedSource : (sources[0]?.id ?? '');
  const size = sizes.find((s) => s.taskSize === pickedSize) ?? sizes[0];
  const hours = safetyStopHours(hoursText);
  const request = launchRequestOf({ studioId, showStyleId, kind, sourceId, scheduled, startLocal, redundant, taskSize: size?.taskSize ?? null, rehearsal, simulators, safetyHoursText: hoursText });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (request === null || pending) return;
    onLaunch(request);
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Launch cloud production</DialogTitle>
        <DialogDescription>Runs in AWS Fargate as a node of your cloud site. Billing runs from launch to stop.</DialogDescription>
      </DialogHeader>
      <StudioPicker
        studios={cloudStudios}
        studioId={studioId}
        disabled={pending}
        onStudio={(id) => {
          setStudioId(id);
          setShowStyleId('');
        }}
      />
      <ShowStylePicker
        id="cloud-show-style"
        label="Show style"
        showStyles={showStyles}
        value={showStyleId}
        studioDefault={studioDefault}
        allowNone={false}
        disabled={pending}
        description={null}
        onChange={setShowStyleId}
      />
      <SourcePicker
        kind={kind}
        sources={sources}
        sourceId={sourceId}
        disabled={pending}
        onKind={(k) => {
          setKind(k);
          setSourceId('');
        }}
        onSource={setSourceId}
      />
      <StartPicker scheduled={scheduled} startLocal={startLocal} disabled={pending} onScheduled={setScheduled} onStart={setStartLocal} />
      <Field>
        <Label htmlFor="cloud-size">Task size</Label>
        <NativeSelect id="cloud-size" value={size?.taskSize ?? ''} onChange={(e) => setSize(sizes.find((s) => s.taskSize === e.target.value)?.taskSize ?? 'small')} disabled={pending || sizes.length === 0}>
          {sizes.map((s) => (
            <option key={s.taskSize} value={s.taskSize}>
              {sizeLabel(s)}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <div className="grid gap-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={redundant} onChange={(e) => setRedundant(e.target.checked)} disabled={pending} />
          Redundant (two tasks in two AZs)
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={rehearsal} onChange={(e) => setRehearsal(e.target.checked)} disabled={pending} />
          Rehearsal
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={simulators} onChange={(e) => setSimulators(e.target.checked)} disabled={pending} />
          Simulated devices (demo)
        </label>
      </div>
      <SafetyStop text={hoursText} valid={hours !== null} disabled={pending} onText={setHours} />
      <Estimate size={size} redundant={redundant} hours={hours} />
      <EgressIps egressIps={egressIps} region={region} />
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={request === null || pending} data-testid="launch-cloud-submit">
          {pending ? 'Launching…' : 'Launch'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function LaunchCloudProductionDialog({ open, ...rest }: LaunchCloudProductionDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) rest.onCancel();
      }}
    >
      <DialogContent data-testid="launch-cloud-dialog" className="max-h-screen overflow-y-auto">
        <LaunchForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
